// apps/web/src/components/ai/ai-context.jsx
// Globaler KI-Assistent: Context liefert isOpen, pageContext (route-basiert).
// PageContext kommt aus flow.areas (zentrale Area-Config).

import { createContext, useContext, useState, useEffect } from "react";
import { Sparkles, X } from "lucide-react";
import { useRoute } from "../../useRoute.js";
import { getPageContext } from "./flows/flow.areas.js";

/**
 * @typedef {{
 *   area: string;
 *   page: string;
 *   labelKey: string;
 *   suggestionKeys: string[];
 * }} PageContext
 */

/**
 * @typedef {{
 *   isOpen: boolean;
 *   setIsOpen: (open: boolean) => void;
 *   pageContext: PageContext;
 *   t: (key: string, params?: Record<string, string | number>) => string;
 *   userDisplayName: string;
 * }} AIContextValue
 */

const AIContext = createContext(/** @type {AIContextValue | null} */ (null));

export function useAI() {
  const ctx = useContext(AIContext);
  if (!ctx) throw new Error("useAI must be used within AIProvider");
  return ctx;
}

// getPageContext(route) wird aus flow.areas.js importiert (zentrale Route → Area-Config).

/**
 * @param {{ children: import('react').ReactNode; t: (key: string, params?: Record<string, string | number>) => string; userDisplayName?: string }} props
 */
export function AIProvider({
  children,
  t,
  userDisplayName = "",
  pageMode = false,
}) {
  const [isOpen, setIsOpen] = useState(pageMode);
  const { route } = useRoute();
  const pageContext = getPageContext(route);

  useEffect(() => {
    if (!pageMode) setIsOpen(false);
  }, [route, pageMode]);

  useEffect(() => {
    if (pageMode) return undefined;
    const onToggle = () => setIsOpen((open) => !open);
    window.addEventListener("tm:ai-toggle-request", onToggle);
    return () => window.removeEventListener("tm:ai-toggle-request", onToggle);
  }, [pageMode]);

  const value = {
    isOpen,
    setIsOpen,
    pageContext,
    t,
    userDisplayName: String(userDisplayName || ""),
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

export function AIFAB() {
  const { isOpen, setIsOpen, t } = useAI();
  const label = isOpen ? t("ai.fab.close") : t("ai.fab.open");

  return (
    <button
      type="button"
      className={["ai-fab", isOpen ? "ai-fab--open" : ""]
        .filter(Boolean)
        .join(" ")}
      aria-label={label}
      aria-expanded={isOpen}
      onClick={() => setIsOpen((open) => !open)}
    >
      {isOpen ? (
        <X className="ai-fab__icon" aria-hidden />
      ) : (
        <Sparkles className="ai-fab__icon" aria-hidden />
      )}
    </button>
  );
}
